import { useState, createContext, useContext, type ReactNode } from 'react';

export interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  disabled?: boolean;
  badge?: string | number;
}

export interface TabsProps {
  items: TabItem[];
  defaultActiveTab?: string;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'underline';
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  tabListClassName?: string;
  tabContentClassName?: string;
}

interface TabsContextType {
  activeTab: string;
  setActiveTab: (tabId: string) => void;
  variant: TabsProps['variant'];
}

const TabsContext = createContext<TabsContextType | undefined>(undefined);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component');
  }
  return context;
};

export const Tabs = ({
  items,
  defaultActiveTab,
  activeTab,
  onTabChange,
  variant = 'default',
  orientation = 'horizontal',
  className = '',
  tabListClassName = '',
  tabContentClassName = ''
}: TabsProps) => {
  
  const [internalActiveTab, setInternalActiveTab] = useState(
    activeTab || defaultActiveTab || items[0]?.id || ''
  );

  const currentActiveTab = activeTab || internalActiveTab;

  const handleTabChange = (tabId: string) => {
    if (activeTab === undefined) {
      setInternalActiveTab(tabId);
    }
    onTabChange?.(tabId);
  };

  const contextValue: TabsContextType = {
    activeTab: currentActiveTab,
    setActiveTab: handleTabChange,
    variant
  };

  const getTabListClassName = () => {
    const baseClasses = {
      horizontal: 'flex',
      vertical: 'flex flex-col'
    };

    const variantClasses = {
      default: 'border-b border-gray-200',
      pills: 'bg-gray-100 rounded-lg p-1',
      underline: 'border-b border-gray-200'
    };

    return `${baseClasses[orientation]} ${variantClasses[variant]} ${tabListClassName}`;
  };

  const getContentClassName = () => {
    const baseClasses = orientation === 'vertical' ? 'flex-1 ml-4' : 'mt-4';
    return `${baseClasses} ${tabContentClassName}`;
  };

  const containerClassName = orientation === 'vertical' 
    ? `flex ${className}`
    : className;

  return (
    <TabsContext.Provider value={contextValue}>
      <div className={containerClassName}>
        {/* Tab List */}
        <div className={getTabListClassName()} role="tablist">
          {items.map((item) => (
            <TabButton 
              key={item.id} 
              item={item}
              orientation={orientation}
            />
          ))}
        </div>

        {/* Tab Content */}
        <div className={getContentClassName()}>
          {items.map((item) => (
            <TabPanel key={item.id} item={item} />
          ))}
        </div>
      </div>
    </TabsContext.Provider>
  );
};

interface TabButtonProps {
  item: TabItem;
  orientation: TabsProps['orientation'];
}

const TabButton = ({ item, orientation }: TabButtonProps) => {
  const { activeTab, setActiveTab, variant } = useTabsContext();
  const isActive = activeTab === item.id;

  const getButtonClassName = () => {
    const baseClasses = 'flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2';

    const orientationClasses = {
      horizontal: 'px-4 py-2',
      vertical: 'w-full px-4 py-3 text-left'
    };

    const variantClasses = {
      default: isActive 
        ? 'border-b-2 border-blue-600 text-blue-600' 
        : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
      pills: isActive
        ? 'bg-white text-blue-600 shadow-sm'
        : 'text-gray-500 hover:text-gray-700 hover:bg-white/50',
      underline: isActive
        ? 'border-b-2 border-blue-600 text-blue-600'
        : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    };

    const sizeClasses = {
      default: variant === 'pills' ? 'rounded-md text-sm' : 'text-sm',
      pills: 'rounded-md text-sm',
      underline: 'text-sm'
    };

    const disabledClasses = item.disabled 
      ? 'opacity-50 cursor-not-allowed pointer-events-none'
      : 'cursor-pointer';

    return `${baseClasses} ${orientationClasses[orientation!]} ${variantClasses[variant!]} ${sizeClasses[variant!]} ${disabledClasses}`;
  };

  const handleClick = () => {
    if (!item.disabled) {
      setActiveTab(item.id);
    }
  };

  return (
    <button
      role="tab"
      aria-selected={isActive}
      aria-controls={`tabpanel-${item.id}`}
      id={`tab-${item.id}`}
      className={getButtonClassName()}
      onClick={handleClick}
      disabled={item.disabled}
    >
      {item.icon && (
        <item.icon className="w-4 h-4 mr-2" />
      )}
      <span>{item.label}</span>
      {item.badge && (
        <span className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
          isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
        }`}>
          {item.badge}
        </span>
      )}
    </button>
  );
};

interface TabPanelProps {
  item: TabItem;
}

const TabPanel = ({ item }: TabPanelProps) => {
  const { activeTab } = useTabsContext();
  const isActive = activeTab === item.id;

  if (!isActive) return null;

  return (
    <div
      role="tabpanel"
      aria-labelledby={`tab-${item.id}`}
      id={`tabpanel-${item.id}`}
      className="focus:outline-none"
    >
      {item.content}
    </div>
  );
};

// Componente simples para casos básicos
export interface SimpleTab {
  label: string;
  content: ReactNode;
  disabled?: boolean;
}

interface SimpleTabsProps {
  tabs: SimpleTab[];
  variant?: TabsProps['variant'];
  className?: string;
}

export const SimpleTabs = ({ tabs, variant = 'default', className }: SimpleTabsProps) => {
  const items: TabItem[] = tabs.map((tab, index) => ({
    id: `tab-${index}`,
    label: tab.label,
    content: tab.content,
    disabled: tab.disabled
  }));

  return (
    <Tabs
      items={items}
      variant={variant}
      className={className}
    />
  );
};